import { useEffect, useState } from 'react'
import { Users, Loader2, Download, Filter, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { api, getApiErrorMessage } from '@/lib/api'
import { PaginationControls } from '@/components/PaginationControls'
import { toast } from 'sonner'

interface WaitlistEntry {
  id: string
  email: string
  display_name: string | null
  source: string
  referrer: string | null
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  status: string
  invited_at: string | null
  converted_at: string | null
  created_at: string
}

const statusColors: Record<string, string> = {
  pending: 'bg-amber-500/10 text-amber-500',
  invited: 'bg-blue-500/10 text-blue-500',
  registered: 'bg-emerald-500/10 text-emerald-500',
  churned: 'bg-gray-500/10 text-gray-500',
}

export function Waitlist() {
  const [entries, setEntries] = useState<WaitlistEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [total, setTotal] = useState(0)
  const [invitingId, setInvitingId] = useState<string | null>(null)
  const [offset, setOffset] = useState(0)
  const [workerConfigured, setWorkerConfigured] = useState<boolean | null>(null)
  const LIMIT = 50

  const fetchEntries = async (newOffset = offset) => {
    setLoading(true)
    try {
      const params: Record<string, unknown> = { limit: LIMIT, offset: newOffset }
      if (statusFilter) params.status = statusFilter
      const res = await api.get('/waitlist', { params })
      setEntries(res.data.entries || [])
      setTotal(res.data.total || 0)
      setOffset(newOffset)
    } catch {
      toast.error('Failed to load waitlist')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEntries(0)
    api.get('/admin/settings')
      .then((res) => setWorkerConfigured(res.data.integrations.waitlist_worker_configured ?? false))
      .catch(() => setWorkerConfigured(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter])

  const handlePageChange = (newOffset: number) => {
    fetchEntries(newOffset)
  }

  const handleInvite = async (email: string) => {
    setInvitingId(email)
    try {
      await api.post('/admin/waitlist/invite', { email })
      toast.success(`Invite sent to ${email}`)
      fetchEntries()
    } catch (err: any) {
      // The backend may have already updated the worker status before failing on
      // its own audit log; refresh the list so the user sees the current state.
      fetchEntries()
      toast.error(getApiErrorMessage(err, 'Failed to send invite'))
    } finally {
      setInvitingId(null)
    }
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const res = await api.get('/waitlist/export', {
        params: statusFilter ? { status: statusFilter } : undefined,
        responseType: 'blob',
      })
      const blob = new Blob([res.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `waitlist-${statusFilter || 'all'}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      toast.success('CSV downloaded')
    } catch {
      toast.error('Failed to export CSV')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-6">
      {workerConfigured === false && (
        <div className="rounded-md border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm text-amber-700">
          <strong>Waitlist worker not configured.</strong> The admin panel is reading from the local
          database. Set <code>CF_WAITLIST_ADMIN_KEY</code> in the API environment and restart the API
          container to load entries from Cloudflare D1.
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Waitlist
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total} total entries
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={exporting || entries.length === 0}
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Download className="h-4 w-4 mr-1" />
          )}
          Export CSV
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <div className="flex gap-1">
          {['', 'pending', 'invited', 'registered', 'churned'].map((s) => (
            <Button
              key={s || 'all'}
              variant={statusFilter === s ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setStatusFilter(s)}
            >
              {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
            </Button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">Email</th>
                  <th className="px-4 py-3 text-left font-medium">Source</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">UTM</th>
                  <th className="px-4 py-3 text-left font-medium">Joined</th>
                  <th className="px-4 py-3 text-left font-medium w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : entries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No waitlist entries
                    </td>
                  </tr>
                ) : (
                  entries.map((entry) => (
                    <tr key={entry.id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-medium">{entry.email}</span>
                        {entry.display_name && (
                          <div className="text-muted-foreground text-xs">{entry.display_name}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {entry.source || '—'}
                        {entry.referrer && (
                          <div className="text-xs">ref: {entry.referrer}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary" className={statusColors[entry.status] || ''}>
                          {entry.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {entry.utm_source || entry.utm_medium || entry.utm_campaign ? (
                          <div className="space-y-0.5">
                            {entry.utm_source && <div>src: {entry.utm_source}</div>}
                            {entry.utm_medium && <div>med: {entry.utm_medium}</div>}
                            {entry.utm_campaign && <div>cmp: {entry.utm_campaign}</div>}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(entry.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          onClick={() => handleInvite(entry.email)}
                          disabled={invitingId === entry.email}
                        >
                          {invitingId === entry.email ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Mail className="h-3.5 w-3.5" />
                          )}
                          <span className="ml-1 text-xs">Invite</span>
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
        <PaginationControls offset={offset} limit={LIMIT} total={total} onPageChange={handlePageChange} />
      </Card>
    </div>
  )
}
